import { Injectable } from '@angular/core';

import { EventOption, EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { BetslipConfig } from '@frontend/sports/common/client-config-data-access';
import { OddsOperations } from '@frontend/sports/odds/feature';
import { BetslipDisplayMode } from '@frontend/sports/types/components/bet-placement';

import { IBetslipState } from '../../../base/store/state';
import { BetslipType } from '../../../core/betslip-type';
import { BetslipV1Pick } from '../../../core/picks/betslip-v1-pick';
import { BetslipV2OptionMarketPick } from '../../../core/picks/betslip-v2-option-market-pick';
import { PickId } from '../../../core/picks/pick-id';
import { IBetslipPickMarket, IPrice, PriceType } from '../../../core/picks/pick-models';
import { createSignedName } from '../../../core/picks/signed-name.model';
import { BetslipV2HorseRaceXCastPick } from '../../../core/picks/sport-specific/betslip-v2-horse-race-picks';
import {
    EachWayInfo,
    IBetslipCalculatorOverrides,
    ICalculatorBetslipInput,
    ICalculatorInput,
    emptyBetslipCalculatorOverrides,
} from '../../bet-generation/models';
import BaseInputBuilder, { BetslipStateInput } from '../../bet-generation/services/base-input-builder';
import ComboBetInputBuilder from '../../bet-generation/services/combo-bet-input-builder';
import EditBetInputBuilder from '../../bet-generation/services/edit-bet-input-builder';
import SingleBetInputBuilder from '../../bet-generation/services/single-bet-input-builder';
import SystemBetInputBuilder from '../../bet-generation/services/system-bet/system-bet-input-builder';
import { getPickPriceIds, isStartingPriceSelected } from '../../picks/services/betslip-pick-helpers';
import BetslipPickUtilsService from '../../picks/services/betslip-pick-utils.service';

const singlePicksSelectorOptionsCreator = (lineSwitcherPickId: PickId) => ({
    picksSelector: (betslipState: BetslipStateInput) =>
        Object.entries(betslipState.types.singleBet.picks).filter(
            ([key, pick]) => lineSwitcherPickId.isEqual(key) && betslipState.picks.pickList.some((p) => p.id.toString() === key),
        ),
});

const comboPicksSelectorOptionsCreator = (lineSwitcherPickId: PickId) => (betslipState: BetslipStateInput) =>
    Object.entries(betslipState.types.comboBet.picks).filter(
        ([key, pick]) =>
            ((pick.isSelected && (betslipState.base.displayMode !== BetslipDisplayMode.Linear || !pick.isLocked)) ||
                lineSwitcherPickId.isEqual(key)) &&
            betslipState.picks.pickList.some((p) => p.id.toString() === key),
    );

const systemPicksSelectorOptionsCreator = (lineSwitcherPickId: PickId) => ({
    picksSelector: (betslipState: BetslipStateInput) =>
        Object.entries(betslipState.types.systemBet.picks).filter(
            ([key, pick]) =>
                ((pick.isSelected && (betslipState.base.displayMode !== BetslipDisplayMode.Linear || !pick.isLocked)) ||
                    lineSwitcherPickId.isEqual(key)) &&
                betslipState.picks.pickList.some((p) => p.id.toString() === key),
        ),
});
@Injectable()
export default class LineSwitcherInputBuilderService extends BaseInputBuilder {
    constructor(
        private singleBetInputBuilder: SingleBetInputBuilder,
        private comboBetInputBuilder: ComboBetInputBuilder,
        private systemBetInputBuilder: SystemBetInputBuilder,
        private editBetInputBuilder: EditBetInputBuilder,
        private betslipPickUtilsService: BetslipPickUtilsService,
        private betslipConfig: BetslipConfig,
    ) {
        super();
    }

    getCalculatorInput(betslipState: BetslipStateInput, lineSwitcherPickId: PickId): ICalculatorInput {
        if (betslipState.types.base.currentSelectedType === null) {
            return { betslip: [] };
        }
        let betslips: Iterable<ICalculatorBetslipInput> = [];
        switch (betslipState.types.base.currentSelectedType) {
            case BetslipType.Single:
                betslips = this.singleBetInputBuilder.getBetGeneratorInput(betslipState, singlePicksSelectorOptionsCreator(lineSwitcherPickId));
                break;
            case BetslipType.Combo:
                betslips = this.comboBetInputBuilder.getBetGeneratorInput(betslipState, {
                    picksSelector: comboPicksSelectorOptionsCreator(lineSwitcherPickId),
                    isSportcastAsComboEnabled: this.betslipConfig.isSportcastAsComboEnabled,
                });
                break;
            case BetslipType.System:
                betslips = this.systemBetInputBuilder.getBetGeneratorInput(
                    betslipState,
                    betslipState.types.systemBet.systemInfo,
                    systemPicksSelectorOptionsCreator(lineSwitcherPickId),
                );
                break;
            case BetslipType.EditBet:
                betslips = Array.from(this.editBetInputBuilder.getBetGeneratorInput(betslipState));
        }

        return { betslip: Array.from(betslips) };
    }

    getOverrides(betslipState: IBetslipState): IBetslipCalculatorOverrides {
        const lineSwitcherState = betslipState.lineSwitcher;
        const newPickId = lineSwitcherState.swapPickState.newPickId;
        const oldPickId = lineSwitcherState.swapPickState.oldPickId;
        const oldPickStringId = lineSwitcherState.swapPickState.oldPickId.toString();
        const options = this.resolveOptions(lineSwitcherState.optionGroup);

        // if there is no newPickId this could be a price update
        // so we get the latest price from state and set it as override
        if (!newPickId) {
            const oldOption = options.find((o) => o.id === oldPickId.optionId && o.online);

            return oldOption
                ? {
                      slipOverrides: [],
                      pickOverrides: {
                          prices: {
                              [oldPickStringId]: {
                                  type: PriceType.Fixed,
                                  nativeOdds: OddsOperations.createOdds(oldOption.nativePrice.toJSON()),
                              },
                          },
                          eachWay: {},
                          replacements: {},
                      },
                  }
                : emptyBetslipCalculatorOverrides;
        }

        const newOption = options.find((o) => o.id === newPickId?.optionId && o.online);
        if (!newOption) {
            return emptyBetslipCalculatorOverrides;
        }

        const pickList =
            betslipState.types.base.currentSelectedType === BetslipType.EditBet
                ? betslipState.types.editBet.picks.pickList
                : betslipState.picks.pickList;
        const oldPick = pickList.find((pick) => pick.id.isEqual(oldPickId));
        if (!oldPick) {
            return emptyBetslipCalculatorOverrides;
        }

        const overrides: IBetslipCalculatorOverrides = {
            slipOverrides: [],
            pickOverrides: {
                prices: {},
                eachWay: {},
                replacements: {},
            },
        };

        const newPick = oldPick.copy();
        newPick.id = newPickId!;
        const price: IPrice = {
            id: newPickId.optionId,
            isVisible: newOption.visible,
            marketId: newOption.optionGroupId,
            type: PriceType.Fixed,
            nativeOdds: OddsOperations.createOdds(newOption.nativePrice.toJSON()),
        };

        newPick.market = { id: newOption.optionGroupId } as IBetslipPickMarket;

        if (BetslipV1Pick.isPick(newPick)) {
            newPick.option = {
                id: newPickId.optionId,
                name: createSignedName(newOption.name),
                price,
            };
        }

        if (BetslipV2OptionMarketPick.isPick(newPick)) {
            newPick.option = {
                id: newPickId.optionId,
                name: createSignedName(newOption.name),
                isVisible: newOption.visible,
                prices: [price],
            };
        }

        let isBanker = false;
        if (betslipState.types.base.currentSelectedType === BetslipType.System) {
            isBanker = !!betslipState.types.systemBet.picks[oldPickStringId]?.isBanker;
        }

        overrides.pickOverrides.replacements![oldPickStringId] = {
            pick: newPick,
            price: newPick.currentPrice || null,
            eachWayInfo: this.getEachWayOverrides(betslipState, oldPickStringId),
            isBanker,
            priceIds: getPickPriceIds(newPick),
            isXCast: BetslipV2HorseRaceXCastPick.isPick(newPick),
            isStartingPrice: isStartingPriceSelected(newPick),
        };

        return overrides;
    }

    private getEachWayOverrides(betslipState: IBetslipState, oldPickId: string): EachWayInfo | null {
        const placeTerms = betslipState.lineSwitcher.optionGroup?.placeTerms;
        const isEachWaySelected = this.betslipPickUtilsService.isEachWaySelected(
            betslipState.types,
            betslipState.types.base.currentSelectedType,
            oldPickId,
        );

        return placeTerms && isEachWaySelected ? { placeTerms } : null;
    }

    resolveOptions(eventOptionGroup?: EventOptionGroup): EventOption[] {
        if (!eventOptionGroup) {
            return [];
        }

        if (eventOptionGroup.subGroups?.length) {
            return eventOptionGroup.options.concat(eventOptionGroup.subGroups.flatMap(this.resolveOptions));
        }

        return eventOptionGroup.options;
    }
}
